import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  Users,
  Search,
  ChevronDown,
  Shield,
  ArrowLeft,
  Crown,
  Zap,
  User,
  Trash2,
} from 'lucide-react';

const PLAN_COLORS = {
  free: 'bg-gray-500/20 text-gray-300',
  pro: 'bg-blue-500/20 text-blue-400',
  studio: 'bg-purple-500/20 text-purple-400',
};

const PLAN_ICONS = {
  free: User,
  pro: Zap,
  studio: Crown,
};

const UserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [changingPlan, setChangingPlan] = useState(null);
  const [filterPlan, setFilterPlan] = useState('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (userId, newPlan) => {
    setChangingPlan(userId);
    try {
      await api.put(`/admin/users/${userId}/plan`, { plan: newPlan });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, subscription_plan: newPlan } : u
      ));
    } catch (err) {
      console.error('Failed to update plan:', err);
    } finally {
      setChangingPlan(null);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    setDeletingId(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Failed to delete user:', err);
      window.alert(`Failed to delete ${userEmail}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = users.filter(u => {
    const matchesSearch = !searchQuery ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.full_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = filterPlan === 'all' || u.subscription_plan === filterPlan;
    return matchesSearch && matchesPlan;
  });

  if (user?.role !== 'admin') return null;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/admin')}
          className="p-2 hover:bg-bg-light rounded-lg transition"
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-5 h-5 text-text-muted" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            User Management
          </h1>
          <p className="text-sm text-text-secondary mt-1">{users.length} registered users</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full pl-10 pr-4 py-2.5 bg-bg-light border border-text-muted/20 rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary/50"
            data-testid="user-search-input"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'free', 'pro', 'studio'].map((plan) => (
            <button
              key={plan}
              onClick={() => setFilterPlan(plan)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition capitalize ${
                filterPlan === plan
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-bg-light border border-text-muted/20 text-text-secondary hover:text-text-primary'
              }`}
              data-testid={`filter-${plan}`}
            >
              {plan === 'all' ? `All (${users.length})` : `${plan} (${users.filter(u => u.subscription_plan === plan).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* User Table */}
      {loading ? (
        <div className="text-center py-12 text-text-muted">Loading users...</div>
      ) : (
        <div className="bg-bg-light rounded-xl border border-text-muted/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-text-muted/15 bg-bg-dark/50">
                  <th className="text-left py-3 px-4 text-text-secondary font-medium">User</th>
                  <th className="text-left py-3 px-4 text-text-secondary font-medium">Plan</th>
                  <th className="text-center py-3 px-4 text-text-secondary font-medium">Analyses</th>
                  <th className="text-center py-3 px-4 text-text-secondary font-medium">Repairs</th>
                  <th className="text-center py-3 px-4 text-text-secondary font-medium">Supports</th>
                  <th className="text-center py-3 px-4 text-text-secondary font-medium">Diagnoses</th>
                  <th className="text-left py-3 px-4 text-text-secondary font-medium">Joined</th>
                  <th className="text-right py-3 px-4 text-text-secondary font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const PlanIcon = PLAN_ICONS[u.subscription_plan] || User;
                  return (
                    <tr key={u.id} className="border-b border-text-muted/10 hover:bg-bg-dark/30 transition">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {u.role === 'admin' && <Shield className="w-4 h-4 text-yellow-400" />}
                          <div>
                            <p className="text-text-primary font-medium">{u.full_name || 'No name'}</p>
                            <p className="text-text-muted text-xs">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize ${PLAN_COLORS[u.subscription_plan] || PLAN_COLORS.free}`}>
                          <PlanIcon className="w-3 h-3" />
                          {u.subscription_plan}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-text-secondary">{u.usage?.stl_analyses || 0}</td>
                      <td className="py-3 px-4 text-center text-text-secondary">{u.usage?.stl_repairs || 0}</td>
                      <td className="py-3 px-4 text-center text-text-secondary">{u.usage?.smart_supports || 0}</td>
                      <td className="py-3 px-4 text-center text-text-secondary">{u.usage?.image_analyses || 0}</td>
                      <td className="py-3 px-4 text-text-muted text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {u.role !== 'admin' && (
                          <div className="flex items-center justify-end gap-2">
                            <div className="relative inline-block">
                              <select
                                value={u.subscription_plan}
                                onChange={(e) => handlePlanChange(u.id, e.target.value)}
                                disabled={changingPlan === u.id}
                                className="appearance-none bg-bg-dark border border-text-muted/20 rounded-lg px-3 py-1.5 pr-7 text-xs text-text-secondary cursor-pointer focus:outline-none focus:border-primary/50 disabled:opacity-50"
                                data-testid={`plan-select-${u.id}`}
                              >
                                <option value="free">Free</option>
                                <option value="pro">Pro</option>
                                <option value="studio">Studio</option>
                              </select>
                              <ChevronDown className="w-3 h-3 text-text-muted absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                            {confirmDeleteId === u.id ? (
                              <div className="flex items-center gap-1" data-testid={`delete-confirm-${u.id}`}>
                                <button
                                  onClick={() => handleDeleteUser(u.id, u.email)}
                                  disabled={deletingId === u.id}
                                  className="px-2.5 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50"
                                  data-testid={`confirm-delete-btn-${u.id}`}
                                >
                                  {deletingId === u.id ? 'Deleting…' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={deletingId === u.id}
                                  className="px-2.5 py-1.5 bg-bg-dark hover:bg-text-muted/10 border border-text-muted/20 text-text-secondary rounded-lg text-xs"
                                  data-testid={`cancel-delete-btn-${u.id}`}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteId(u.id)}
                                title={`Delete ${u.email}`}
                                className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition"
                                data-testid={`delete-user-btn-${u.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-8 text-text-muted text-sm">No users match your search.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
